/**
 * Cashero Dexie database schema.
 *
 * Single database instance shared between wallet and mint modes.
 * Tables are only used by their respective mode (wallet tables ignored in mint mode, vice versa).
 */

import Dexie, { type Table } from 'dexie';
import type { Proof } from '@/lib/cashu/proof';
import type { Keyset, KeysetPrivate } from '@/lib/cashu/keyset';
import type { MoneroNode } from '@/lib/monero/nodes';

// ====================================================================
// Wallet-side types
// ====================================================================

/** A mint entry in the user's wallet */
export interface WalletMintEntry {
  /** Mint's Nostr pubkey (hex) */
  npub: string;
  /** Display name (from announcement) */
  name: string;
  /** Description (from announcement) */
  description: string;
  /** Cached keysets */
  keysets: Keyset[];
  /** Cached fees */
  fees: { depositPercent: number; withdrawFlat: number };
  /** Required confirmations for deposits */
  requiredConfirmations: number;
  /** When this mint was added */
  addedAt: number;
}

/** A proof stored in the wallet */
export interface StoredProof extends Proof {
  /** The mint this proof belongs to */
  mintNpub: string;
  /** When this proof was acquired */
  acquiredAt: number;
}

/** Transaction history record */
export interface TransactionRecord {
  /** Unique ID */
  id: string;
  /** Transaction type */
  type: 'deposit' | 'withdraw' | 'send' | 'receive';
  /** Associated mint npub */
  mintNpub: string;
  /** Amount in piconero */
  amount: number;
  /** Unix timestamp */
  timestamp: number;
  /** Current status */
  status: 'pending' | 'incoming' | 'confirmed' | 'failed';
  /** Monero transaction hash (for deposit/withdraw) */
  txHash?: string;
  /** Ecash token string (for send/receive) */
  tokenString?: string;
  /** Counterparty npub (for Nostr DM transfers) */
  counterpartyNpub?: string;
  /** Fee paid (piconero) */
  fee?: number;
}

/** Pending operation waiting for mint response */
export interface PendingOperation {
  /** Request ID (uuid) */
  requestId: string;
  /** Operation type */
  op: string;
  /** Mint npub */
  mintNpub: string;
  /** Created at timestamp */
  createdAt: number;
  /** Request payload (JSON) */
  payload: string;
  /** Status */
  status: 'pending' | 'processing' | 'completed' | 'failed';
}

// ====================================================================
// Mint-side types
// ====================================================================

/** Mint's own configuration persisted in IndexedDB */
export interface StoredMintConfig {
  /** Unique key (always "config") */
  id: string;
  /** Mint's Nostr identity */
  nsec: string;
  npub: string;
  /** Monero wallet seed phrase */
  moneroSeed: string;
  moneroAddress: string;
  moneroViewKey: string;
  moneroSpendKey: string;
  /** Monero node URI */
  moneroNodeUri: string;
  /** Network type */
  networkType: string;
  /** Deposit fee percent */
  depositFeePercent: number;
  /** Withdraw flat fee (piconero) */
  withdrawFeeFlat: number;
  /** Required confirmations */
  requiredConfirmations: number;
  /** Max denomination power */
  maxDenomination: number;
  /** Whether setup is complete */
  setupComplete: boolean;
  /** Monero restore height (0 = new wallet) */
  restoreHeight: number;
}

/** Pending deposit awaiting Monero confirmation */
export interface PendingDeposit {
  /** Request ID from wallet */
  requestId: string;
  /** Wallet's npub */
  walletNpub: string;
  /** Subaddress index */
  subaddressIndex: number;
  /** Subaddress string */
  subaddress: string;
  /** Expected amount (piconero) */
  expectedAmount: number;
  /** Blinded messages to sign on confirmation */
  blindedMessages: string; // JSON string of BlindedMessageData[]
  /** Current status */
  status: 'waiting' | 'incoming' | 'confirmed' | 'done';
  /** Transaction hash (when detected) */
  txHash?: string;
  /** Current confirmations */
  confirmations: number;
  /** Required confirmations */
  requiredConfirmations: number;
  /** When deposit was requested */
  createdAt: number;
  /** When tx was detected */
  detectedAt?: number;
  /** When tx was confirmed */
  confirmedAt?: number;
}

/** Spent secret hash */
export interface SpentSecret {
  /** SHA-256 hash of the secret */
  secretHash: string;
  /** When it was spent */
  spentAt: number;
  /** Which wallet spent it (pubkey) */
  spenderNpub?: string;
}

// ====================================================================
// Database
// ====================================================================

export class CasheroDB extends Dexie {
  // Wallet tables
  walletConfig!: Table<{ id: string; displayUnit: 'xmr' | 'piconero'; updatedAt: number }, string>;
  walletMints!: Table<WalletMintEntry, string>;
  walletProofs!: Table<StoredProof & { id?: number }, number>;
  walletHistory!: Table<TransactionRecord, string>;
  walletPendingOps!: Table<PendingOperation, string>;

  // Mint tables
  mintConfig!: Table<StoredMintConfig, string>;
  mintKeysets!: Table<KeysetPrivate & { id: string }, string>;
  pendingDeposits!: Table<PendingDeposit, string>;
  spentSecrets!: Table<SpentSecret, string>;

  constructor() {
    super('cashero');

    this.version(1).stores({
      // Wallet tables
      walletConfig: 'id',
      walletMints: 'npub',
      walletProofs: '++id, mintNpub',
      walletHistory: 'id, mintNpub, type, timestamp',
      walletPendingOps: 'requestId, mintNpub, op',

      // Mint tables
      mintConfig: 'id',
      mintKeysets: 'id',
      pendingDeposits: 'requestId, status, subaddress',
      spentSecrets: 'secretHash, spentAt',
    });
  }
}

/** Singleton database instance */
export const db = new CasheroDB();
